// src/screens/AccesosScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { isModuleEnabled } from '../utils/saasConfig';
import { 
  IoArrowBack, 
  IoPeopleOutline, 
  IoCubeOutline, 
  IoBuildOutline, 
  IoLockClosedOutline, 
  IoArrowForward, 
  IoArchiveOutline, 
  IoTimeOutline,
  IoHomeOutline,
  IoStorefrontOutline,
  IoWalletOutline,
  IoSettingsOutline,
  IoChatbubblesOutline,
  IoCashOutline,
  IoLogOutOutline,
  IoWarningOutline,
  IoKeyOutline,
  IoLockOpenOutline
} from 'react-icons/io5';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import BotonPanico from '../components/Safety/BotonPanico';
import './AccesosScreen.css';

const AccessCard = ({ to, icon, title, description, theme, badgeCount, locked = false }) => (
  <Link to={to} className={`access-card ${theme} ${locked ? 'saas-locked' : ''}`} style={locked ? { opacity: 0.65, border: '1px dashed #cbd5e1', cursor: 'pointer' } : {}}>
    <div className="card-icon-wrapper" style={locked ? { background: '#f1f5f9', color: '#94a3b8' } : {}}>
      {locked ? <IoLockClosedOutline size={28} /> : icon}
      {badgeCount > 0 && (
        <span className="home-badge">{badgeCount}</span>
      )}
    </div>
    <div className="card-content">
      <h2 style={locked ? { color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' } : {}}>
        {title} 
        {locked && (
          <span style={{ fontSize: '0.65rem', background: '#fee2e2', color: '#ef4444', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
            Bloqueado
          </span>
        )}
      </h2>
      <p>{description}</p>
    </div>
    <div className="card-arrow">
      <IoArrowForward size={24} />
    </div>
  </Link>
);

function AccesosScreen() {
  const navigate = useNavigate();
  const { isMoroso, userData } = useAuth();
  const [pendingPackages, setPendingPackages] = useState(0);

  useEffect(() => {
    if (!userData || !userData.condominioId) return;

    if (userData.rol === 'residente' || userData.rol === 'comite') {
      const qPackages = query(
        collection(db, "paquetes"),
        where("condominioId", "==", userData.condominioId),
        where("estado", "==", "en_caseta")
      );
      const unsubPackages = onSnapshot(qPackages, (snap) => {
        let count = 0;
        snap.docs.forEach(docSnap => {
          const pkg = docSnap.data();
          if (pkg.detallesCasa === userData.numeroCasa || pkg.detallesCasa === userData.residencia) {
            count++;
          }
        });
        setPendingPackages(count);
      });
      return () => unsubPackages();
    }
  }, [userData]);

  const isLockersEnabled = isModuleEnabled(userData?.condoData, 'paqueteria');

  return (
    <div className="ks-page">
      {/* ── SIDEBAR (Escritorio) ── */}
      <KondoSidebar activeRoute="/accesos" />

      <main className="ks-main">
        {/* Brand Header (Sticky) */}
        <div className="ks-header-sticky-wrapper">
          <div className="ks-header-content">
            <KondoBrandHeader />
          </div>
        </div>

        <div className="ks-scroll-area">
          <div className="muro-header-stitch">
            {/* Hero Card - Estilo Muro Social */}
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Tus Accesos</h1>
                <p>Control de acceso total</p>
              </div>
              <IoLockClosedOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div className="accesos-container">
            {/* Tarjeta Destacada Permanente: Mi Llave (Exenta de bloqueo de morosidad) */}
            <div style={{ marginBottom: '20px' }}>
              <AccessCard
                to="/accesos/mi-llave"
                icon={<IoKeyOutline />}
                title="Mi Llave"
                description="Tu centro de acceso peatonal inteligente: genera tu código dinámico, configura tu tarjeta RFID y registra tu rostro."
                theme="theme-mi-llave"
              />
            </div>

            {/* Grid Area with Overlay Logic */}
            <div style={{ position: 'relative', marginTop: '10px' }}>
              {isMoroso && (
                <div className="accesos-bloqueo">
                  <div className="bloqueo-content">
                    <IoLockClosedOutline className="lock-icon-large" />
                    <h2>Acceso Restringido</h2>
                    <p>Tu cuenta presenta adeudos pendientes. Para generar nuevos accesos o solicitar servicios, por favor regulariza tu situación.</p>
                    <button className="btn-secondary-action" style={{ marginTop: 20, width: '100%', justifyContent: 'center' }} onClick={() => navigate('/pagar-cuota')}>
                      Pagar Cuotas / Regularizar
                    </button>
                  </div>
                </div>
              )}

              <div className="accesos-grid" style={isMoroso ? { filter: 'blur(4px)', pointerEvents: 'none' } : {}}>
                <AccessCard
                  to="/accesos/control-remoto"
                  icon={<IoLockOpenOutline />}
                  title="Control de Acceso Remoto"
                  description="Apertura inteligente de plumas, portones y accesos vehiculares desde tu celular."
                  theme="theme-mi-llave"
                />

                <AccessCard
                  to="/accesos/visitas"
                  icon={<IoPeopleOutline />}
                  title="Visitas"
                  description="Registra visitas frecuentes/ocasionales y genera códigos QR de acceso para tus invitados."
                  theme="theme-visitas"
                />

                <AccessCard
                  to="/accesos/envios"
                  icon={<IoCubeOutline />}
                  title="Paquetería"
                  description="Gestiona la recepción de pedidos y autoriza entregas de paquetería."
                  theme="theme-envios"
                />

                <AccessCard
                  to="/accesos/proveedores"
                  icon={<IoBuildOutline />}
                  title="Servicios"
                  description="Directorio de proveedores, historial de servicios y gestión de mantenimientos."
                  theme="theme-proveedores"
                />

                 <AccessCard
                  to="/paqueteria"
                  icon={<IoArchiveOutline />}
                  title="Lockers"
                  description="Paquetes recibidos y almacenados por vigilancia."
                  theme="theme-blue"
                  badgeCount={pendingPackages}
                  locked={!isLockersEnabled}
                />

                <AccessCard
                  to="/mis-accesos"
                  icon={<IoTimeOutline />}
                  title="Mi Historial"
                  description="Bitácora personal de todas tus visitas y envíos recibidos."
                  theme="theme-gray"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── FLOAT NAV ISLAND (Móvil) ── */}
        <KondoBottomNav activeRoute="/accesos" />
      </main>
    </div>
  );
}

export default AccesosScreen;